import React, {
  ReactNode,
  ElementType,
  Children,
  cloneElement,
  isValidElement,
} from "react";
import { useScrollAnimation, AnimationType } from "@/hooks/useScrollAnimation";

interface StaggerAnimationProps {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  animation?: AnimationType;
  threshold?: number;
  triggerOnce?: boolean;
  staggerDelay?: number;
  disabled?: boolean;
  viewport?: {
    amount?: number;
    margin?: string;
  };
}

/**
 * StaggerAnimation Component
 *
 * Animates children elements with a staggered delay for a cascading effect.
 * Each child element will animate with an incrementing delay.
 *
 * @example
 * ```tsx
 * <StaggerAnimation animation="fade-in-up" staggerDelay={100}>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </StaggerAnimation>
 * ```
 */
export const StaggerAnimation: React.FC<StaggerAnimationProps> = ({
  children,
  className = "",
  as: Component = "div",
  animation = "fade-in-up",
  threshold = 0.3,
  triggerOnce = true,
  staggerDelay = 100,
  disabled = false,
  viewport,
}) => {
  const { ref, isInView } = useScrollAnimation({
    animation: "fade-in", // Container uses simple fade
    threshold,
    triggerOnce,
    disabled,
    viewport,
  });

  // Convert children to array and add delays
  const childrenArray = Children.toArray(children);

  const staggeredChildren = childrenArray.map((child, index) => {
    if (!isValidElement(child)) {
      return child;
    }

    const delay = index * staggerDelay;
    const animationClass = `animate-${animation} ${isInView ? "in-view" : ""} ${delay > 0 ? `anim-delay-${delay}` : ""}`;

    // Clone child and add animation classes
    return cloneElement(child, {
      ...child.props,
      className: `${child.props.className || ""} ${animationClass}`.trim(),
      style: {
        ...(child.props.style as React.CSSProperties),
        animationDelay: `${delay}ms`,
      },
    });
  });

  return (
    <Component ref={ref} className={className}>
      {staggeredChildren}
    </Component>
  );
};

/**
 * StaggerFadeInUp - Stagger animation with fade-in-up
 */
export const StaggerFadeInUp: React.FC<
  Omit<StaggerAnimationProps, "animation">
> = (props) => <StaggerAnimation {...props} animation="fade-in-up" />;

/**
 * StaggerFadeIn - Stagger animation with fade-in
 */
export const StaggerFadeIn: React.FC<
  Omit<StaggerAnimationProps, "animation">
> = (props) => <StaggerAnimation {...props} animation="fade-in" />;

/**
 * StaggerScaleUp - Stagger animation with scale-up
 */
export const StaggerScaleUp: React.FC<
  Omit<StaggerAnimationProps, "animation">
> = (props) => <StaggerAnimation {...props} animation="scale-up" />;
